import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { applyProviderOffers } from "../lib/provider-import-actions";
import { invalidateCredentialVaultRecord } from "../lib/credential-vault-invalidation";
import type {
  ProviderOfferAction,
  ProviderOfferSelection,
  ProviderImportScope,
  ProviderOfferDocument,
} from "../components/provider-imports/provider-import-types";

export function useProviderImportTransport(scope: ProviderImportScope) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { organizationBusinessUnitId, providerUserId, expectedAffiliationId } =
    scope;
  const single = useMutation(
    trpc.credentials.importProviderCredentialForBu.mutationOptions(),
  );
  const batch = useMutation(
    trpc.credentials.importProviderCredentialsForBu.mutationOptions(),
  );
  const dismiss = useMutation(
    trpc.credentials.dismissProviderCredentialOfferForBu.mutationOptions(),
  );
  const offeredDocument = useMutation(
    trpc.credentials.getProviderCredentialOfferDocumentUrlForBu.mutationOptions(),
  );
  const heldDocument = useMutation(
    trpc.credentials.getDocumentUrlForBu.mutationOptions(),
  );
  const request = useRef(0);
  const [preview, setPreview] = useState<{
    document: ProviderOfferDocument;
    offerId: string;
    offered: boolean;
    url: string | null;
    error: string | null;
  } | null>(null);
  useEffect(
    () => () => {
      request.current += 1;
    },
    [],
  );

  async function refresh() {
    await Promise.all([
      invalidateCredentialVaultRecord({
        queryClient,
        trpc,
        scope: { ...scope, kind: "organization" },
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.credentials.listVaultFilesForBu.queryKey(scope),
      }),
      queryClient.invalidateQueries({
        queryKey:
          trpc.credentials.listProviderCredentialOffersForBu.queryKey(scope),
      }),
      queryClient.invalidateQueries({
        queryKey:
          trpc.credentials.getProviderCredentialOfferForBu.queryKey(scope),
      }),
    ]);
  }

  const loadOffer = useCallback(
    (offerId: string) => {
      return queryClient.fetchQuery({
        ...trpc.credentials.getProviderCredentialOfferForBu.queryOptions({
          organizationBusinessUnitId,
          providerUserId,
          ...(expectedAffiliationId ? { expectedAffiliationId } : {}),
          offerId,
        }),
        staleTime: 0,
      });
    },
    [
      queryClient,
      trpc,
      organizationBusinessUnitId,
      providerUserId,
      expectedAffiliationId,
    ],
  );

  function applyOffers(input: {
    action: ProviderOfferAction;
    offers: ProviderOfferSelection[];
    onProgress: (completed: number) => void;
  }) {
    return applyProviderOffers({
      ...input,
      transport: {
        importOne: (item) => single.mutateAsync({ ...scope, ...item }),
        importBatch: (items) => batch.mutateAsync({ ...scope, items }),
        dismissOne: (item) => dismiss.mutateAsync({ ...scope, ...item }),
        refresh,
      },
    });
  }

  async function openPreview(input: {
    document: ProviderOfferDocument;
    offerId: string;
    offered: boolean;
  }) {
    const currentRequest = ++request.current;
    setPreview({ ...input, url: null, error: null });
    try {
      const result = input.offered
        ? await offeredDocument.mutateAsync({
            ...scope,
            offerId: input.offerId,
            documentId: input.document.id,
            asAttachment: false,
          })
        : await heldDocument.mutateAsync({
            ...scope,
            documentId: input.document.id,
            asAttachment: false,
          });
      if (currentRequest === request.current) {
        setPreview({ ...input, url: result.signedUrl, error: null });
      }
    } catch (error) {
      if (currentRequest === request.current) {
        setPreview({
          ...input,
          url: null,
          error:
            error instanceof Error
              ? error.message
              : "Could not load this document.",
        });
      }
    }
  }

  async function download() {
    if (!preview) {
      return;
    }
    const input = {
      ...scope,
      documentId: preview.document.id,
      asAttachment: true,
    };
    const result = preview.offered
      ? await offeredDocument.mutateAsync({
          ...input,
          offerId: preview.offerId,
        })
      : await heldDocument.mutateAsync(input);
    window.open(result.signedUrl, "_blank", "noopener,noreferrer");
  }

  return {
    loadOffer,
    applyOffers,
    refresh,
    openPreview,
    download,
    preview,
    isPending: single.isPending || batch.isPending || dismiss.isPending,
    isPreviewLoading: offeredDocument.isPending || heldDocument.isPending,
    closePreview: () => {
      request.current += 1;
      setPreview(null);
    },
  };
}
